import type { DailyReflection } from '@/types/reflection';

interface ValuesTags {
  expressed: string[];
  suppressed: string[];
}

/**
 * Safely parse valuesTags from the Record<string, unknown> shape stored in DailyReflection.
 * Returns { expressed: [], suppressed: [] } for any malformed input.
 */
export function parseValuesTags(raw: Record<string, unknown>): ValuesTags {
  const expressed = Array.isArray(raw?.expressed)
    ? raw.expressed.filter((v): v is string => typeof v === 'string')
    : [];
  const suppressed = Array.isArray(raw?.suppressed)
    ? raw.suppressed.filter((v): v is string => typeof v === 'string')
    : [];
  return { expressed, suppressed };
}

/**
 * Return the most common value name from an array, with its count.
 * Returns null if the array is empty.
 */
export function computeTopValue(values: string[]): { name: string; count: number } | null {
  if (values.length === 0) return null;

  const freq = new Map<string, number>();
  for (const v of values) {
    freq.set(v, (freq.get(v) || 0) + 1);
  }

  let topName = '';
  let topCount = 0;
  for (const [name, count] of freq) {
    if (count > topCount) {
      topName = name;
      topCount = count;
    }
  }

  return { name: topName, count: topCount };
}

/**
 * Filter reflections to those within the last N calendar days (inclusive of today).
 */
export function filterReflectionsToLastNDays(
  reflections: DailyReflection[],
  days: number,
  now: Date = new Date()
): DailyReflection[] {
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - (days - 1));
  cutoff.setHours(0, 0, 0, 0);
  const cutoffStr = cutoff.toISOString().slice(0, 10); // YYYY-MM-DD

  return reflections.filter((r) => r.reflectionDate >= cutoffStr);
}
